/**
 * Tax Summary Component
 * Displays tax reporting data with short-term/long-term gains
 */

import { useTaxSummary } from '../api/client';

export function TaxSummary() {
  const currentYear = new Date().getFullYear();
  const { data, isLoading, error } = useTaxSummary(currentYear);

  if (isLoading) {
    return (
      <div className="card">
        <div className="loading">Loading tax summary...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">Error loading tax summary: {error.message}</div>
      </div>
    );
  }

  const tax = data?.taxSummary;
  if (!tax) return null;

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Tax Summary ({currentYear})</h2>
        <span className={`card-badge ${tax.totalNetGainLoss >= 0 ? 'badge-success' : 'badge-danger'}`}>
          {tax.totalNetGainLoss >= 0 ? '+' : ''}$
          {tax.totalNetGainLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      {/* Short-Term (< 365 days) */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>
          Short-Term Capital Gains/Losses (Taxed as ordinary income)
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="metric">
            <div className="metric-label">Gains</div>
            <div className="metric-value positive">
              +${tax.shortTermGains.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="metric">
            <div className="metric-label">Losses</div>
            <div className="metric-value negative">
              -${tax.shortTermLosses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="metric">
            <div className="metric-label">Net</div>
            <div className={`metric-value ${tax.netShortTerm >= 0 ? 'positive' : 'negative'}`}>
              {tax.netShortTerm >= 0 ? '+' : ''}$
              {tax.netShortTerm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>

      {/* Long-Term (>= 365 days) */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>
          Long-Term Capital Gains/Losses (Preferential tax rates)
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="metric">
            <div className="metric-label">Gains</div>
            <div className="metric-value positive">
              +${tax.longTermGains.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="metric">
            <div className="metric-label">Losses</div>
            <div className="metric-value negative">
              -${tax.longTermLosses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="metric">
            <div className="metric-label">Net</div>
            <div className={`metric-value ${tax.netLongTerm >= 0 ? 'positive' : 'negative'}`}>
              {tax.netLongTerm >= 0 ? '+' : ''}$
              {tax.netLongTerm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>

      {/* Wash Sales */}
      {tax.washSaleLossesDisallowed > 0 && (
        <div
          style={{
            padding: '1rem',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid var(--accent-yellow)',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}
        >
          <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--accent-yellow)' }}>
            Wash Sale Adjustments
          </div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            <div>
              Losses Disallowed: $
              {tax.washSaleLossesDisallowed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ marginTop: '0.25rem' }}>
              Active Blocks: {tax.activeWashSaleBlocks}
            </div>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
            Wash sale losses are added to cost basis of replacement shares
          </div>
        </div>
      )}

      {/* Summary */}
      <div style={{ paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <div className="metric">
            <div className="metric-label">Total Net Gain/Loss</div>
            <div className={`metric-value ${tax.totalNetGainLoss >= 0 ? 'positive' : 'negative'}`}>
              {tax.totalNetGainLoss >= 0 ? '+' : ''}$
              {tax.totalNetGainLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
          <div className="metric">
            <div className="metric-label">YTD Realized</div>
            <div className={`metric-value ${tax.ytdRealized >= 0 ? 'positive' : 'negative'}`}>
              {tax.ytdRealized >= 0 ? '+' : ''}$
              {tax.ytdRealized.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
        IRS Form 8949 & Schedule D ready. Consult a tax professional for filing.
      </div>
    </div>
  );
}
